import React from 'react';

// ============================================================================
// Switch + CheckTile — the form-controls standard for booleans. Native
// <input type="checkbox"> is banned on this stack (the indicator vanishes
// under the Tailwind CDN — Walkthrough stairs/elevator toggles first,
// Users & Roles overrides next).
//
//   <Switch>    on/off that takes effect immediately: packing on, waive
//               the fee, is-draft. 44×26 track. (form-controls.md "On/off: A")
//   <CheckTile> many-of lists where you tick several: waivers, rooms
//               present, crew on the job. The whole 44px tile is the
//               target; the check mark is decoration. ("On/off: B")
//
// Both: checked, onChange(bool), label, description?, disabled, size
// ('md' | 'sm'), ariaLabel (when there's no visible label).
// ============================================================================

export function Switch({ checked = false, onChange, label, description, disabled = false, size = 'md', ariaLabel, className = '', labelClassName = '' }) {
    const on = !!checked;
    const track = size === 'sm' ? 'w-9 h-5' : 'w-11 h-[26px]';
    const knob = size === 'sm' ? 'w-4 h-4 top-0.5 left-0.5' : 'w-5 h-5 top-[3px] left-[3px]';
    const shift = size === 'sm' ? 'translate-x-4' : 'translate-x-[18px]';
    return (
        <button
            type="button"
            role="switch"
            aria-checked={on}
            aria-label={ariaLabel || (typeof label === 'string' ? label : undefined)}
            disabled={disabled}
            onClick={() => !disabled && onChange?.(!on)}
            className={`inline-flex items-center gap-2.5 ${size === 'sm' ? 'min-h-[32px]' : 'min-h-[40px]'} text-left select-none focus:outline-none group disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
        >
            <span
                aria-hidden="true"
                className={`relative flex-shrink-0 ${track} rounded-full transition-colors ${on ? 'bg-emerald-600' : 'bg-slate-300'} group-focus-visible:ring-2 group-focus-visible:ring-red-500/30 group-focus-visible:ring-offset-1`}
            >
                <span className={`absolute ${knob} rounded-full bg-white shadow-[0_1px_2px_rgba(0,0,0,0.25)] transition-transform ${on ? shift : ''}`} />
            </span>
            {(label || description) && (
                <span className="min-w-0">
                    {label && <span className={`block font-semibold text-slate-800 ${size === 'sm' ? 'text-[12px]' : 'text-sm'} ${labelClassName}`}>{label}</span>}
                    {description && <span className="block text-xs text-slate-500 leading-snug">{description}</span>}
                </span>
            )}
        </button>
    );
}

export function CheckTile({ checked = false, onChange, label, description, disabled = false, size = 'md', ariaLabel, className = '' }) {
    const on = !!checked;
    const box = size === 'sm' ? 'w-[18px] h-[18px] text-[11px]' : 'w-[22px] h-[22px] text-[14px]';
    return (
        <button
            type="button"
            role="checkbox"
            aria-checked={on}
            aria-label={ariaLabel || (typeof label === 'string' ? label : undefined)}
            disabled={disabled}
            onClick={() => !disabled && onChange?.(!on)}
            className={`flex items-center gap-2.5 w-full text-left rounded-lg border bg-white select-none transition-colors ${
                size === 'sm' ? 'min-h-[36px] px-2.5 py-1.5' : 'min-h-[44px] px-3 py-2'
            } ${on ? 'border-red-400' : 'border-slate-200 hover:border-slate-300'} focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500/30 disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
        >
            <span
                aria-hidden="true"
                className={`flex-shrink-0 ${box} rounded-md border-2 grid place-items-center font-black text-white transition-colors ${on ? 'bg-red-600 border-red-600' : 'border-slate-300'}`}
            >
                {on ? '✓' : ''}
            </span>
            {(label || description) && (
                <span className="min-w-0">
                    {label && <span className={`block font-medium text-slate-800 ${size === 'sm' ? 'text-[12px]' : 'text-sm'}`}>{label}</span>}
                    {description && <span className="block text-xs text-slate-500 leading-snug">{description}</span>}
                </span>
            )}
        </button>
    );
}

export default Switch;
